import { ProposalTable } from "./proposal-table";
import React from "react";
import {
  Banner, Button, Card, DiffView, FormField, Input, Select,
  StatusPill, VersionStamp,
} from "@/ds";
import { FACT_TONE, Icon, StatusOf, cx, eur, num } from "@/lib";
import type { PositionOccasion, PositionStaffingFormat, PositionsView, RequestDetail } from "@/api";
import { useLang, useT } from "../../i18n";
import {
  draftPaneEmptyState,
  positionProposalPresentation,
} from "../screen-workspace.logic";
import { activeJoins, segmentHasMark, segmentMarkKeys, segmentText, type EvidenceMark } from "../evidence-highlight";
import { DraftReveal } from "./draft-reveal";
import { DraftRecipient } from "./draft-recipient";
import { RichTextEditor, type RichTextEditorValue } from "./rich-text-editor";
import { DraftTools } from "./draft-tools";
import reviewStyles from "./email-review.module.css";
import { extrasEligibilityDiagnostic } from "./extras-eligibility";
import { positionResolutionPresentation } from "./position-resolution";
// Word-level diff (LCS) for the inline draft editor.
function diffWords(a: string, b: string): Array<{ type: string; text: string }> {
  const A = (a || "").split(/(\s+)/), B = (b || "").split(/(\s+)/);
  const n = A.length, m = B.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const parts: Array<{ type: string; text: string }> = []; let i = 0, j = 0;
  const push = (type: string, text: string) => { const last = parts[parts.length - 1]; if (last && last.type === type) last.text += text; else parts.push({ type, text }); };
  while (i < n && j < m) {
    if (A[i] === B[j]) { push("same", B[j]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("del", A[i]); i++; }
    else { push("ins", B[j]); j++; }
  }
  while (i < n) push("del", A[i++]);
  while (j < m) push("ins", B[j++]);
  return parts;
}

// ==================== EVIDENCE HIGHLIGHTING (#376) ====================
/** Body text with the evidence marks rendered as hoverable highlights. */
function HighlightedText({ body, marks, active, onHover }: {
  body: string;
  marks: EvidenceMark[];
  active: string | null;
  onHover: (key: string | null) => void;
}) {
  const segments = React.useMemo(() => segmentText(body, marks), [body, marks]);
  return (
    <>
      {segments.map((seg, i) => {
        if (!seg.markKey) return <React.Fragment key={i}>{seg.text}</React.Fragment>;
        const tone = FACT_TONE[seg.status ?? ""] ?? "neutral";
        return (
          <mark
            key={i}
            className={cx("ws-mark", `ws-mark--${tone}`, segmentHasMark(seg, active) && "ws-mark--active")}
            data-mark={seg.markKey}
            data-marks={segmentMarkKeys(seg)}
            data-joins={activeJoins(segments, i, active)}
            onMouseEnter={() => onHover(seg.markKey!)}
            onMouseLeave={() => onHover(null)}
          >{seg.text}</mark>
        );
      })}
    </>
  );
}

type RichTextChunk = { node: Text; start: number; end: number };
type RichTextMarkRange = { start: number; end: number; key: string; keys: string; status: string; active: boolean; joins?: string };

const RICH_TEXT_BLOCK_ENDS = new Set(["P", "LI"]);

/** Add evidence marks without flattening the server-canonicalized rich document. */
function highlightedCanonicalHtml(bodyHtml: string, marks: EvidenceMark[], active: string | null): string {
  if (typeof DOMParser === "undefined") return bodyHtml;
  const document = new DOMParser().parseFromString(`<body>${bodyHtml}</body>`, "text/html");
  const chunks: RichTextChunk[] = [];
  let projectedText = "";
  const appendBreak = () => {
    if (projectedText && !projectedText.endsWith("\n")) projectedText += "\n";
  };
  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? "";
      if (text) {
        const start = projectedText.length;
        projectedText += text;
        chunks.push({ node: node as Text, start, end: projectedText.length });
      }
      return;
    }
    if (!(node instanceof Element)) return;
    if (node.tagName === "BR") {
      projectedText += "\n";
      return;
    }
    for (const child of Array.from(node.childNodes)) visit(child);
    if (RICH_TEXT_BLOCK_ENDS.has(node.tagName)) appendBreak();
  };
  for (const child of Array.from(document.body.childNodes)) visit(child);

  const ranges: RichTextMarkRange[] = [];
  let offset = 0;
  const segments = segmentText(projectedText, marks);
  segments.forEach((segment, index) => {
    const end = offset + segment.text.length;
    if (segment.markKey) {
      ranges.push({
        start: offset,
        end,
        key: segment.markKey,
        keys: segmentMarkKeys(segment) ?? segment.markKey,
        status: segment.status ?? "",
        active: segmentHasMark(segment, active),
        joins: activeJoins(segments, index, active),
      });
    }
    offset = end;
  });

  for (const chunk of chunks) {
    const overlaps = ranges.filter((range) => range.end > chunk.start && range.start < chunk.end);
    if (!overlaps.length) continue;
    const fragment = document.createDocumentFragment();
    const source = chunk.node.data;
    let cursor = 0;
    for (const range of overlaps) {
      const start = Math.max(0, range.start - chunk.start);
      const end = Math.min(source.length, range.end - chunk.start);
      if (start > cursor) fragment.append(document.createTextNode(source.slice(cursor, start)));
      const mark = document.createElement("mark");
      const tone = FACT_TONE[range.status] ?? "neutral";
      mark.className = cx("ws-mark", `ws-mark--${tone}`, range.active && "ws-mark--active");
      mark.dataset.mark = range.key;
      mark.dataset.marks = range.keys;
      if (range.joins) mark.dataset.joins = range.joins;
      mark.textContent = source.slice(start, end);
      fragment.append(mark);
      cursor = end;
    }
    if (cursor < source.length) fragment.append(document.createTextNode(source.slice(cursor)));
    chunk.node.replaceWith(fragment);
  }
  return document.body.innerHTML;
}

function evidenceMarkKey(target: EventTarget | null): string | null {
  if (!(target instanceof Node)) return null;
  const element = target instanceof Element ? target : target.parentElement;
  return element?.closest("mark[data-mark]")?.getAttribute("data-mark") ?? null;
}

function HighlightedRichHtml({ bodyHtml, marks, active, onHover }: {
  bodyHtml: string;
  marks: EvidenceMark[];
  active: string | null;
  onHover: (key: string | null) => void;
}) {
  const highlightedHtml = React.useMemo(
    () => highlightedCanonicalHtml(bodyHtml, marks, active),
    [active, bodyHtml, marks],
  );
  return (
    <div
      data-testid="draft-reviewed-html"
      onMouseOver={(event) => {
        const previous = evidenceMarkKey(event.relatedTarget);
        const next = evidenceMarkKey(event.target);
        if (next !== previous) onHover(next);
      }}
      onMouseOut={(event) => {
        const previous = evidenceMarkKey(event.target);
        const next = evidenceMarkKey(event.relatedTarget);
        if (next !== previous) onHover(next);
      }}
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
    />
  );
}

// ============================ DRAFT ============================
export function DraftPanel({ draft, aiOn, guided = false, artifactErrorInChecklist = false, extractionFailed = false, generating = false, analysisStage = null, processingLabel, incomingState, onDisplayed, draftSender, draftRecipient, editing, subject, body, bodyHtml, bodyChanged, setSubject, onRichTextChange, overtakenByNewerMessage = false, pendingReviewActionable = false, marks = [], activeMark = null, onMarkHover, revealEligible = false, claimFirstView, onAttachmentsChanged, recipientEditor, historicalStatus, onOpenDetails }: {
  draft: RequestDetail["draft"]; aiOn: boolean;
  historicalStatus?: "stale" | "rejected";
  extractionFailed?: boolean;
  /** The shared Sol status line owns processing/failure guidance. */
  guided?: boolean;
  /** Suppress only an artifact error already explained by the visible send checklist. */
  artifactErrorInChecklist?: boolean;
  /** #678: a run is in flight, so the pane shows the wait instead of an idle prompt. */
  generating?: boolean;
  processingLabel?: string;
  incomingState?: RequestDetail["incoming_work"];
  onDisplayed?: () => void;
  /** Pipeline stage the run is on, when known. */
  analysisStage?: number | null;
  draftSender: string; draftRecipient: string;
  editing: boolean; subject: string; body: string; bodyHtml: string; bodyChanged: boolean;
  setSubject: (v: string) => void; onRichTextChange: (value: RichTextEditorValue) => void;
  overtakenByNewerMessage?: boolean;
  /** The current action area already explains this draft's pending review requirement. */
  pendingReviewActionable?: boolean;
  marks?: EvidenceMark[]; activeMark?: string | null; onMarkHover?: (key: string | null) => void;
  /** #804: this presentation is the current, unsent, non-stale draft, so it may be revealed. */
  revealEligible?: boolean;
  /** Records the view and answers whether this caller is the one that may animate it. */
  claimFirstView?: (draftId: string) => Promise<boolean>;
  onAttachmentsChanged?: () => Promise<void> | void;
  onOpenDetails?: () => void;
  recipientEditor?: React.ComponentProps<typeof DraftRecipient>["editor"];
}) {
  const t = useT();
  if (!draft && guided) return <section className={reviewStyles.draftEmail} aria-label={t("ws.review.draftReply")}>
    <div className={reviewStyles.emailScroll}>
      <p className={reviewStyles.empty}>{t("solGuide.emptyDraft")}</p>
      {generating && <div className={reviewStyles.draftGeneratingBar} aria-hidden="true"><span /><span /><span /></div>}
    </div>
  </section>;
  if (!draft) {
    const emptyState = draftPaneEmptyState({ extractionFailed, generating, aiOn });
    const heading = emptyState === "blocked"
      ? t("ws.draftBlocked")
      : emptyState === "generating"
      ? t("ws.draftGenerating")
      : incomingState?.state === "needs_attention" ? t("incoming.attention")
      : incomingState?.state === "settled" ? t("incoming.noReply") : t("ws.noDraftYet");
    return (
      <section
        className={reviewStyles.draftEmail}
        data-testid="draft-email-pane"
        data-draft-empty-state={emptyState}
        data-draft-state={emptyState === "idle" ? "empty" : emptyState === "ai_off" ? "blocked" : emptyState}
        aria-label={`${t("ws.review.draftReply")} ${heading}`}
      >
        <div className={reviewStyles.emailScroll}>
          <p className={reviewStyles.replyEmptyHeading}>{heading}</p>
          {emptyState === "blocked"
            ? <Banner tone="danger" title={t("ws.extractionFailed")}>{t("ws.extractionFailedBody")}</Banner>
            : emptyState === "generating"
            // aria-live so the wait is announced; the animation itself is decorative and is
            // reduced to a static bar under prefers-reduced-motion (email-review.module.css).
            ? (
              <div
                className={reviewStyles.draftGenerating}
                data-testid="draft-generating"
                role="status"
                aria-live="polite"
              >
                <div className={reviewStyles.draftGeneratingBar} aria-hidden="true">
                  <span /><span /><span />
                </div>
                <p className={reviewStyles.draftGeneratingLead}>{t("ws.draftGeneratingBody")}</p>
                <p className={reviewStyles.empty}>
                  {processingLabel ?? (analysisStage !== null
                    ? t("ws.draftGeneratingStage", { n: String(analysisStage) })
                    : t("ws.draftGeneratingQueued"))}
                </p>
              </div>
            )
            : emptyState === "ai_off"
            ? <Banner tone="warning" title={t("ws.aiDisabled")}>{t("ws.aiDisabledBody")}</Banner>
            : <p className={reviewStyles.empty}>{t(incomingState?.state === "needs_attention" ? "incoming.attentionBody"
              : incomingState?.state === "settled" ? "incoming.noReplyBody" : "ws.noDraftBody")}</p>}
        </div>
      </section>
    );
  }

  const displaySubject = (draft.outbound_artifact ? draft.outbound_artifact.subject : draft.subject) || t("common.noSubject");
  const reviewedSender = draft.outbound_artifact
    ? [draft.outbound_artifact.from_name, `<${draft.outbound_artifact.from}>`].filter(Boolean).join(" ")
    : draftSender;
  const reviewedRecipient = draft.outbound_artifact?.to ?? draft.recipient?.effective_address ?? "—";
  const showReviewStatus = Boolean(historicalStatus) || overtakenByNewerMessage
    || draft.review_state !== "pending"
    || !pendingReviewActionable;
  // Named once: the reveal plays a copy of this text in front of it and then hands over, so the
  // two paths must render exactly the same thing or the handover would show as a jump.
  const replyHtml = draft.reply_body_html ?? draft.body_html;
  const replyText = draft.reply_body_text ?? draft.body_text ?? "";
  const hasRichFormatting = /<(?:strong|em|ul|ol|a)\b/i.test(replyHtml || "");
  const authoritativeBody = hasRichFormatting && replyHtml
    ? marks.length > 0 && onMarkHover
      ? (
        <HighlightedRichHtml
          bodyHtml={replyHtml}
          marks={marks}
          active={activeMark}
          onHover={onMarkHover}
        />
      )
      : <div data-testid="draft-reviewed-html" dangerouslySetInnerHTML={{ __html: replyHtml }} />
    : marks.length > 0 && onMarkHover
      ? <HighlightedText body={replyText} marks={marks} active={activeMark} onHover={onMarkHover} />
      : replyText;

  return (
    <section
      className={reviewStyles.draftEmail}
      data-testid="draft-email-pane"
      data-draft-state="ready"
      aria-label={`${t("ws.review.draftReply")} ${reviewedSender}`}
      data-artifact-hash={draft.outbound_artifact?.content_hash}
    >
      <div className={reviewStyles.emailScroll}>
        {draft.outbound_artifact_error && !artifactErrorInChecklist && <Banner tone="warning" title={t(
          draft.outbound_artifact_error === "APPROVER_NAME_REQUIRED" ? "ws.approverNameRequired" : "ws.artifactUnavailable"
        )}>
          {t(draft.outbound_artifact_error === "APPROVER_NAME_REQUIRED" ? "ws.approverNameRequiredBody"
            : draft.outbound_artifact_error === "DRAFT_CLOSING_REGENERATION_REQUIRED" ? "ws.closingRegenerationRequiredBody"
            : "ws.artifactUnavailableBody")}
        </Banner>}
        <dl className={reviewStyles.messageAddresses} data-testid="draft-message-meta">
          <dt>{t("ws.review.from")}</dt>
          <dd><span data-mail-address-content="from">{reviewedSender}</span></dd>
          <dt className={recipientEditor ? reviewStyles.recipientLabel : undefined}>{t("ws.review.to")}</dt>
          <dd><DraftRecipient draft={draft} address={reviewedRecipient} editor={recipientEditor} /></dd>
        </dl>
        <div
          className={cx(reviewStyles.messageSubject, reviewStyles.draftSubject, showReviewStatus && reviewStyles.draftSubjectWithStatus)}
          data-testid="draft-message-subject"
          title={editing ? (subject || t("common.noSubject")) : displaySubject}
        >
          <span className={reviewStyles.messageFieldLabel}>{t("ws.review.subject")}</span>
          <span className={reviewStyles.messageSubjectValue}>
            {editing
              ? <Input aria-label={t("ws.review.subject")} value={subject} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)} />
              : <span className={reviewStyles.messageSubjectText}>{displaySubject}</span>}
          </span>
          {showReviewStatus && <div className={reviewStyles.draftStatus} data-testid="draft-review-status">
            {historicalStatus === "stale" ? <StatusPill tone="info" label={t("solGuide.previousDraft")} size="sm" /> : overtakenByNewerMessage
              ? <StatusPill tone="info" label={t("ws.draftOvertakenByNewerMessage")} size="sm" />
              : <StatusOf kind="review" value={draft.review_state} size="sm" />}
          </div>}
        </div>
        {editing ? (
          <div className={reviewStyles.draftEditor}>
            <RichTextEditor
              key={`${draft.id}:${draft.version}`}
              initialHtml={bodyHtml || replyHtml}
              initialText={replyText}
              onChange={onRichTextChange}
            />
            {bodyChanged && body === replyText && (
              <p className={reviewStyles.formattingChanged} role="status">
                {t("ws.richText.formattingChanged")}
              </p>
            )}
            <DiffView title={t("ws.changesVsOriginal")} parts={diffWords(replyText, body)} />
          </div>
        ) : (
          <div
            className={`${reviewStyles.messageBody} ${reviewStyles.draftBody}`}
            data-testid="draft-message-body"
            role="region"
            aria-label={t("ws.review.replyText")}
          >
            <DraftReveal
              draftId={draft.id}
              body={replyText}
              eligible={revealEligible && !hasRichFormatting}
              claimFirstView={claimFirstView}
              onDisplayed={onDisplayed}
            >
              {authoritativeBody}
            </DraftReveal>
          </div>
        )}
        {draft.closing && <div className={`${reviewStyles.messageBody} ${reviewStyles.draftClosing}`} data-testid="draft-reply-closing">
          {draft.closing.text}
        </div>}
        <DraftTools key={draft.id} draft={draft} editing={editing} onChanged={onAttachmentsChanged} onOpenDetails={onOpenDetails} />
      </div>
    </section>
  );
}

function MenuOccasionPreparation({ preparation, disabled, onSave }: {
  preparation: NonNullable<PositionsView["menu_preparation"]>;
  disabled?: boolean;
  onSave: (occasion: PositionOccasion | null, revision: number) => Promise<boolean>;
}) {
  const t = useT();
  const [choice, setChoice] = React.useState(preparation.occasion);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setChoice(preparation.occasion); }, [preparation.occasion, preparation.request_revision]);
  if (preparation.available_occasions.length === 0) return null;
  return <section className="pos-configuration">
    <FormField label={t("ws.occasionLabel")} helper={t("ws.menuOccasionHint")} htmlFor="menu-occasion">
      <Select id="menu-occasion" value={choice ?? ""} disabled={disabled || saving}
        options={[{ value: "", label: t("ws.menuOccasionRequired") },
          ...preparation.available_occasions.map((value) => ({ value, label: t(`ws.occasion.${value}`) }))]}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setChoice((event.target.value || null) as PositionOccasion | null)} />
    </FormField>
    <Button size="sm" variant="secondary" disabled={disabled || saving || choice === null && preparation.occasion === null}
      onClick={async () => {
        setSaving(true);
        try { await onSave(choice, preparation.request_revision); } finally { setSaving(false); }
      }}>{t(saving ? "common.loading" : "ws.saveMenuOccasion")}</Button>
  </section>;
}

// ============================ POSITIONEN ============================
export function PositionsPanel({
  positions: pos,
  loading: displayLoading,
  bpEnabled,
  occasion,
  onOccasionChange,
  staffingFormat,
  onStaffingFormatChange,
  durationHours,
  onDurationHoursChange,
  includeStaffing,
  onIncludeStaffingChange,
  technicalPreview,
  onReviewFacts,
  onSaveMenuOccasion,
  preparationDisabled,
  onApplyPreparation,
  eventOccasion,
  statusNotice,
  statusLabel,
  onRefresh,
}: {
  eventOccasion?: React.ReactNode;
  statusNotice?: React.ReactNode;
  statusLabel?: string | null;
  onRefresh?: () => void;
  onApplyPreparation?: () => void;
  positions: PositionsView | null;
  onSaveMenuOccasion?: (occasion: PositionOccasion | null, revision: number) => Promise<boolean>;
  preparationDisabled?: boolean;
  loading: boolean;
  bpEnabled: boolean;
  occasion: PositionOccasion | null;
  onOccasionChange: (occasion: PositionOccasion | null) => void;
  staffingFormat: PositionStaffingFormat | null;
  onStaffingFormatChange: (format: PositionStaffingFormat | null) => void;
  durationHours: number | null;
  onDurationHoursChange: (hours: number | null) => void;
  includeStaffing: boolean;
  onIncludeStaffingChange: (include: boolean) => void;
  technicalPreview?: React.ReactNode;
  onReviewFacts?: () => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const responseControlsCurrent =
    (pos?.occasion ?? null) === occasion &&
    (pos?.staffing_format ?? null) === staffingFormat &&
    (pos?.duration_hours ?? null) === durationHours &&
    (pos?.include_staffing ?? false) === includeStaffing;
  const blocked = !pos || !(pos.commercial || pos.alternatives?.length);
  // Early/manual responses do not echo proposal controls, but their resolved request signature
  // still makes their server reasons safe to show for the current request.
  const structuredExtrasDiagnostic = displayLoading
    ? null
    : extrasEligibilityDiagnostic(pos, t, lang);
  const enrichment = displayLoading ? [] : (pos?.needs_enrichment || []);
  const presentation = positionProposalPresentation({
    loading: displayLoading,
    eligible: pos?.eligible === true || Boolean(pos?.commercial?.lines.length),
    positionCount: pos?.commercial?.lines.length ?? 0,
    enrichmentCount: pos?.scope?.components.filter((item) => item.status === "manual").length ?? enrichment.length,
  });
  const summary = t(
    presentation.summaryKey,
    presentation.summaryCount === undefined
      ? undefined
      : { n: presentation.summaryCount },
  );
  const quantityMetadataCurrent = !displayLoading && responseControlsCurrent;
  const resolution = positionResolutionPresentation(pos?.resolution);
  const resolutionAction = resolution?.target === "facts"
    ? onReviewFacts
    : undefined;

  return (
    <section className="ws-secondary-section ws-positions-section">
      <div className="row-wrap" style={{ justifyContent: "space-between", marginBottom: "var(--space-4)" }}>
        <h3 style={{ margin: 0, color: "var(--text-strong)", fontSize: "var(--text-base)" }}>{t("ws.positions")}</h3>
        {statusLabel !== null && <StatusPill size="sm" tone="neutral" label={statusLabel ?? summary} />}
        {onRefresh && <Button size="sm" variant="ghost" onClick={onRefresh}>{t("workflow.refreshStatus")}</Button>}
      </div>
      <div>
        {pos?.menu_preparation && onSaveMenuOccasion && (
          <MenuOccasionPreparation preparation={pos.menu_preparation} disabled={preparationDisabled || displayLoading}
            onSave={onSaveMenuOccasion} />
        )}
        {resolution && (
          <Banner tone="warning" title={t(resolution.titleKey)} data-testid="position-resolution">
            <p>{t(resolution.bodyKey)}</p>
            {resolutionAction && (
              <Button size="sm" variant="secondary" onClick={resolutionAction}>
                {t(resolution.actionKey)}
              </Button>
            )}
          </Banner>
        )}
        {!displayLoading && pos?.scope && !pos.commercial && (
          <section aria-label={t("ws.scopeTitle")}>
            <h4>{t("ws.scopeTitle")}</h4>
            <ul>
              {pos.scope.components.map((item) => <li key={item.component}>
                <strong>{t(`ws.scopeComponent.${item.component}`)}</strong>{": "}{t(`ws.scopeStatus.${item.status}`)}
                {item.status === "manual" && <> — {t(`ws.scopeReason.${item.reason}`)}</>}
              </li>)}
            </ul>
            {!pos.eligible && <p className="pos-business-guidance">{t("ws.scopeBlocked")}</p>}
          </section>
        )}
        {!displayLoading && !blocked && !pos?.eligible && pos?.reason && (
          <p className="pos-business-guidance">{t("ws.noProposalReason", { reason: pos.reason })}</p>
        )}
        {presentation.showEnrichmentWarning && !pos?.scope && (
          <Banner
            tone="warning"
            title={t("ws.positionsEnrichmentWarning")}
            data-testid="positions-enrichment-warning"
          >
            {t("ws.positionsEnrichmentSummary", { n: enrichment.length })}
          </Banner>
        )}
        {statusNotice ?? (displayLoading ? <p className="subtle" role="status" aria-busy="true">{t("workspace.readPositions")}</p> : blocked ? (
          <div className="pos-blocked"><Icon name="alert" size={18} /><div>{pos?.reason ? (pos.engine === "single_agent" ? t(`workflow.error.${pos.reason}`, undefined, t("workflow.noProposal")) : t("ws.noProposalReason", { reason: pos.reason })) : t("ws.noProposal")}</div></div>
        ) : (
          <>
            {pos!.commercial && <ProposalTable proposal={pos!.commercial} engine={pos!.engine ?? "deterministic"} />}
            {pos!.alternatives?.map((proposal, index) => <details key={proposal.id} open><summary>{t("workflow.alternative", { n: index + 1 })}</summary>
              <ProposalTable proposal={proposal} engine={pos!.engine ?? "deterministic"} /></details>)}
            {!bpEnabled && <div className="bp-note"><Icon name="lock" size={13} />{t("ws.priceEstimate")}</div>}
          </>
        ))}
        {eventOccasion}
        {structuredExtrasDiagnostic && <p className="pos-business-guidance">{structuredExtrasDiagnostic}</p>}
        {Boolean(pos?.available_occasions?.length || pos?.available_staffing_formats?.length) && (
          <section className="pos-configuration" aria-labelledby="positions-configuration-title">
            <h4 id="positions-configuration-title">{t("ws.positionsConfiguration")}</h4>
            {!pos?.menu_preparation && Boolean(pos?.available_occasions?.length) && (
              <div className="pos-occasion">
                <FormField label={t("ws.occasionLabel")} helper={t("ws.occasionHint")} htmlFor="positions-occasion">
                  <Select
                    id="positions-occasion"
                    disabled={preparationDisabled}
                    value={occasion ?? ""}
                    options={[
                      { value: "", label: t("ws.occasionFallback") },
                      ...pos!.available_occasions!.map((value) => ({
                        value,
                        label: t(`ws.occasion.${value}`),
                      })),
                    ]}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      onOccasionChange((event.target.value || null) as PositionOccasion | null);
                    }}
                  />
                </FormField>
                <div className="pos-occasion__meta">
                  {quantityMetadataCurrent && pos?.quantity_per_person && (
                    <span className="neo-eyebrow">
                      {t("ws.perPersonQuantity", {
                        n: num(pos.quantity_per_person.value),
                        unit: t(`ws.quantityUnit.${pos.quantity_per_person.unit}`),
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}
            {Boolean(pos?.available_staffing_formats?.length) && (
              <div className="pos-staffing-controls">
                <FormField label={t("ws.staffingFormatLabel")} helper={t("ws.staffingFormatHint")} htmlFor="positions-staffing-format">
                  <Select
                    id="positions-staffing-format"
                    disabled={preparationDisabled}
                    value={staffingFormat ?? ""}
                    options={[
                      { value: "", label: t("ws.staffingFormatRequired") },
                      ...pos!.available_staffing_formats!.map((value) => ({
                        value,
                        label: t(`ws.staffingFormat.${value}`),
                      })),
                    ]}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      onStaffingFormatChange((event.currentTarget.value || null) as PositionStaffingFormat | null);
                    }}
                  />
                </FormField>
                <FormField label={t("ws.durationHoursLabel")} helper={t("ws.durationHoursHint")} htmlFor="positions-duration-hours">
                  <Input
                    id="positions-duration-hours"
                    disabled={preparationDisabled}
                    type="number"
                    min={0.25}
                    max={72}
                    step={0.25}
                    value={durationHours ?? ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const value = event.currentTarget.value;
                      onDurationHoursChange(value === "" ? null : Number(value));
                    }}
                  />
                </FormField>
                <div className="pos-staffing-meta">
                  <label className="pos-staffing-toggle">
                    <input
                      type="checkbox"
                      checked={includeStaffing}
                      disabled={preparationDisabled}
                      onChange={(event) => onIncludeStaffingChange(event.currentTarget.checked)}
                    />
                    <span>{t("ws.includeStaffing")}</span>
                  </label>
                </div>
              </div>
            )}
          </section>
        )}
        {onApplyPreparation && <div className="stack-sm"><p className="muted">{t(pos?.engine === "single_agent" ? "workflow.paidChanges" : "workspace.preparationHint")}</p>
          <Button size="sm" disabled={preparationDisabled} onClick={onApplyPreparation}>{t("workspace.applyPreparation")}</Button></div>}
        {technicalPreview && <p className="bp-note"><Icon name="lock" size={13} />{t("ws.review.bpPreviewOnly")}</p>}
        <details className="pos-technical-details">
          <summary>{t("ws.positionsTechnicalDetails")}</summary>
          {displayLoading ? <p className="muted">{t("common.loading")}</p> : (
            <div className="pos-technical-content">
              {pos?.reason && <p>{t("ws.noProposalReason", { reason: pos.reason })}</p>}
              {Boolean(pos?.diagnostics?.length) && (
                <ul>{pos!.diagnostics!.map((diagnostic, index) => <li key={index}>{diagnostic}</li>)}</ul>
              )}
              {enrichment.length > 0 && (
                <ul>{enrichment.map((item, index) => <li key={index}>{t("ws.enrichmentNeeded", { n: item })}</li>)}</ul>
              )}
              {quantityMetadataCurrent && pos?.quantity_ruleset_version && (
                <VersionStamp label={t("ws.quantityRuleset")} value={pos.quantity_ruleset_version} icon={false} />
              )}
              {responseControlsCurrent && pos?.staffing_policy_version && (
                <VersionStamp label={t("ws.staffingPolicyVersion")} value={pos.staffing_policy_version} icon={false} />
              )}
              {technicalPreview}
            </div>
          )}
        </details>
      </div>
    </section>
  );
}

// ============================ BANKETTPROFI ============================
export function BankettProfiPanel({ positions, loading, reference }: {
  positions: PositionsView | null;
  loading: boolean;
  reference: string | null;
}) {
  const t = useT();
  const content = positions?.bp_preview?.content ?? null;

  return (
    <section className="ws-secondary-section ws-export-section">
      <div className="row-wrap" style={{ justifyContent: "space-between", marginBottom: "var(--space-4)" }}>
        <h3 style={{ margin: 0, color: "var(--text-strong)", fontSize: "var(--text-base)" }}>{t("ws.bpExport")}</h3>
        <StatusPill size="sm" tone="neutral" label={t("ws.review.previewOnly")} />
      </div>
      <div>
        <div className="bp-card">
        <div className="bp-card__head">
          <Icon name="layers" size={15} className="subtle" />
          <span className="neo-eyebrow">{reference ? t("ws.bpDryRun", { ref: reference }) : t("ws.bpDryRunNoRef")}</span>
          <span className="spacer" />
        </div>
        <div className="bp-card__body">
          {loading ? <div className="muted" style={{ fontSize: "var(--text-sm)" }}>{t("common.loading")}</div>
            : content ? <div className="bp-preview">{content}</div>
              : <div className="muted" style={{ fontSize: "var(--text-sm)" }}>{t("ws.bpNoExport")}</div>}
          <div className="row-wrap" style={{ marginTop: "var(--space-3)" }}>
            <span className="bp-note"><Icon name="lock" size={13} />{t("ws.review.bpPreviewOnly")}</span>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
